import axios from "axios";
import appToast from "../components/appToast";
import {
  getAccessToken,
  getRefreshToken,
  setTokens,
  clearTokens,
} from "../utils/cookies";
import { addCSRFToHeaders, requiresCSRFProtection } from "../utils/csrf";

const BASE_URL = import.meta.env.VITE_BASE_URL;

// ─── Axios instance ────────────────────────────────────────────────────────
const axiosInstance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
});

// ─── Request interceptor — attach Bearer token and CSRF protection ──────────────
axiosInstance.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    // Add CSRF protection for state-changing requests
    if (requiresCSRFProtection(config.url, config.method)) {
      addCSRFToHeaders(config.headers);
    }

    // Let axios set Content-Type automatically for FormData (includes boundary)
    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// ─── Token refresh state — prevent concurrent refresh storms ─────────────
let isRefreshing = false;
let refreshQueue = []; // [{ resolve, reject }]

function processQueue(error, token = null) {
  refreshQueue.forEach(({ resolve, reject }) =>
    error ? reject(error) : resolve(token),
  );
  refreshQueue = [];
}

// ─── Response interceptor — silent refresh on 401 ────────────────────────
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;

    // Only attempt refresh once per request and only on 401
    if (error.response?.status !== 401 || original._retry) {
      return Promise.reject(error);
    }

    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      _forceLogout();
      return Promise.reject(error);
    }

    if (isRefreshing) {
      // Queue this request until refresh completes
      return new Promise((resolve, reject) => {
        refreshQueue.push({ resolve, reject });
      }).then((newToken) => {
        original.headers["Authorization"] = `Bearer ${newToken}`;
        return axiosInstance(original);
      });
    }

    original._retry = true;
    isRefreshing = true;

    try {
      const { data } = await axios.post(
        `${BASE_URL}/api/auth/getNewToken`,
        { refresh_token: refreshToken },
        { withCredentials: true },
      );

      const newAccessToken = data.tokens.access_token;
      const newRefreshToken = data.tokens.refresh_token;

      setTokens(newAccessToken, newRefreshToken);

      axiosInstance.defaults.headers.common["Authorization"] =
        `Bearer ${newAccessToken}`;
      original.headers["Authorization"] = `Bearer ${newAccessToken}`;

      processQueue(null, newAccessToken);
      return axiosInstance(original);
    } catch (refreshError) {
      processQueue(refreshError, null);
      _forceLogout();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

function _forceLogout() {
  appToast.error("Session expired. Please log in again.");
  clearTokens();
  // Best-effort server-side logout — fire and forget
  axios
    .post(`${BASE_URL}/api/auth/logoff`, {}, { withCredentials: true })
    .catch(() => {});
  window.location.href = "/";
}

// ─── apiFetch shim ────────────────────────────────────────────────────────
/**
 * Drop-in replacement for the old fetch-based apiFetch.
 * Returns a Response-like object so hooks.js needs zero changes.
 *
 * Signature: apiFetch(url, options?, clearUserFn?)
 *   - url        relative path, e.g. "api/auth/getuserinfo"
 *   - options    { method, body, headers }  (same shape as fetch options)
 *   - clearUserFn  legacy param — no longer needed; kept for compat
 */
// eslint-disable-next-line no-unused-vars
async function apiFetch(url, options = {}, clearUserFn = null) {
  const method = (options.method || "GET").toUpperCase();
  const isFormData = options.body instanceof FormData;

  // Convert fetch-style body to axios data
  let data;
  if (options.body !== undefined) {
    data = isFormData ? options.body : options.body;
    // hooks.js passes JSON.stringify(body) for non-FormData — parse it back
    if (!isFormData && typeof options.body === "string") {
      try {
        data = JSON.parse(options.body);
      } catch {
        data = options.body;
      }
    }
  }

  try {
    const headers = { ...options.headers };

    // Add CSRF protection for state-changing requests
    if (requiresCSRFProtection(url, method)) {
      addCSRFToHeaders(headers);
    }

    const response = await axiosInstance.request({
      url: url.startsWith("/") ? url : `/${url}`,
      method,
      data,
      headers,
    });

    // Return a Response-like object that hooks.js expects
    return _toResponseLike(response);
  } catch (err) {
    if (err.response) {
      // HTTP error — return as Response-like with ok=false
      return _toResponseLike(err.response);
    }
    // Network error / logout redirect already handled by interceptor
    return null;
  }
}

/**
 * Wraps an axios response into a fetch Response-like object.
 * Provides: ok, status, json(), text()
 */
function _toResponseLike(axiosResponse) {
  const { status, data } = axiosResponse;
  const ok = status >= 200 && status < 300;
  const body = data;
  return {
    ok,
    status,
    json: () => Promise.resolve(body),
    text: () =>
      Promise.resolve(typeof body === "string" ? body : JSON.stringify(body)),
  };
}

export { axiosInstance };
export default apiFetch;
